import React, { useState } from 'react';
import { Shield, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

/**
 * Prompt the admin for a fresh TOTP code before a privileged action.
 *
 * Usage:
 *   const { promptFor2FA, TwoFactorPromptModal } = useTwoFactorPrompt();
 *   ...
 *   const code = await promptFor2FA();
 *   if (!code) return; // user cancelled
 *   axios.post(url, body, { headers: { 'X-2FA-Code': code, Authorization: `Bearer ${token}` }});
 *   ...
 *   <TwoFactorPromptModal />
 */
export function useTwoFactorPrompt() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [resolveFn, setResolveFn] = useState(null);
  const navigate = useNavigate();

  const promptFor2FA = () => {
    setCode('');
    setOpen(true);
    return new Promise((resolve) => {
      setResolveFn(() => resolve);
    });
  };

  const close = (value) => {
    setOpen(false);
    setBusy(false);
    if (resolveFn) {
      resolveFn(value);
      setResolveFn(null);
    }
  };

  const TwoFactorPromptModal = () => {
    if (!open) return null;
    return (
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
        onClick={() => close(null)}
        data-testid="twofa-modal"
      >
        <div
          className="bg-neutral-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <div>
              <div className="font-bold">Two-factor required</div>
              <div className="text-xs text-neutral-400">Enter your 6-digit authenticator code.</div>
            </div>
          </div>
          <Input
            autoFocus
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.length === 6) {
                setBusy(true);
                close(code);
              }
            }}
            placeholder="123456"
            className="bg-black/40 border-white/10 text-center tracking-[0.6em] text-xl font-mono"
            data-testid="twofa-input"
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="ghost"
              onClick={() => close(null)}
              disabled={busy}
              className="text-neutral-400 hover:text-white"
              data-testid="twofa-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => { setBusy(true); close(code); }}
              disabled={busy || code.length !== 6}
              className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold"
              data-testid="twofa-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Verify
            </Button>
          </div>
          <div className="mt-4 text-xs text-neutral-500 text-center">
            Haven&apos;t set up 2FA yet?{' '}
            <button
              onClick={() => { close(null); navigate('/profile'); }}
              className="text-[#00F0FF] hover:underline"
              data-testid="twofa-enrol-link"
            >
              Enrol here
            </button>
          </div>
        </div>
      </div>
    );
  };

  return { promptFor2FA, TwoFactorPromptModal };
}

/**
 * Helper: run an axios call, auto-intercept 401/428 2FA errors, prompt
 * for a TOTP code, then retry once with the X-2FA-Code header.
 */
export async function callWith2FA(axiosCall, promptFor2FA) {
  try {
    return await axiosCall({});
  } catch (err) {
    const status = err.response?.status;
    const msg = err.response?.data?.detail || '';
    const is2FA = status === 428 || (status === 401 && /2fa|two-factor/i.test(msg));
    if (!is2FA) throw err;
    if (status === 428) {
      toast.error(msg);
      throw err;
    }
    const code = await promptFor2FA();
    if (!code) {
      const cancelErr = new Error('2FA cancelled');
      cancelErr.cancelled = true;
      throw cancelErr;
    }
    return await axiosCall({ 'X-2FA-Code': code });
  }
}
